import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Shield, BarChart3, FileText, Scale, Clock, CheckCircle2 } from 'lucide-react';

const LOGO_URL = '/gantt-logo-blue.png';

const FEATURES = [
  { icon: FileText, label: 'Contract Management', desc: 'NEC, JBCC & FIDIC' },
  { icon: Scale, label: 'Dispute Resolution', desc: 'Full case management' },
  { icon: BarChart3, label: 'AI-Powered Analytics', desc: 'Intelligent insights' },
  { icon: Clock, label: 'Deadline Tracking', desc: 'Automated alerts' },
  { icon: Shield, label: 'Close-Out Engine', desc: 'Defect & snagging' },
  { icon: CheckCircle2, label: 'RBAC Permissions', desc: 'Role-based access' },
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      toast.success('Login successful');
      navigate('/');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8" style={{ backgroundColor: '#f0f4f8' }}>
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="flex items-center justify-center mb-8">
              <img src={LOGO_URL} alt="Gantt" className="h-20 w-auto" />
            </div>
            <h2 className="text-3xl font-barlow font-semibold uppercase tracking-tight mb-2 text-center">Sign In</h2>
            <p className="text-slate-600 text-center">Access your construction project management dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 rounded-sm border-slate-200" required />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 rounded-sm border-slate-200" required />
              </div>
            </div>
            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800 hover:underline" data-testid="forgot-password-link">Forgot Password?</Link>
            </div>
            <Button type="submit" data-testid="login-submit-button" className="w-full bg-[#1e3a5f] text-white hover:bg-[#16304d] rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3 shadow-sm active:scale-95 transition-transform" disabled={loading}>
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="bg-blue-50 border border-blue-100 rounded-sm p-4 text-center" data-testid="subscription-note">
            <p className="text-sm text-slate-600">This platform is available to <span className="font-semibold text-slate-800">subscribed users</span> only.</p>
            <p className="text-sm text-slate-500 mt-1">Interested in a subscription?{' '}<Link to="/#contact" className="text-blue-600 font-medium hover:underline" data-testid="contact-form-link">Fill in our contact form</Link> and we'll be in touch.</p>
          </div>
        </div>
      </div>

      {/* Right - Info Panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0c2340 0%, #1e3a5f 40%, #2a5080 100%)' }}>
        {/* Subtle geometric pattern */}
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 50%)' }} />
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-white w-full">
          <div className="max-w-lg">
            <h3 className="text-3xl font-barlow font-bold uppercase mb-4 leading-tight">Construction Project<br />Management</h3>
            <p className="text-base text-blue-200 leading-relaxed mb-8">
              Proactively manage project timelines, approvals, and contractual processes with intelligent tracking and automated workflows. Designed to identify potential delays early, improve coordination, and keep projects aligned with NEC, JBCC &amp; FIDIC requirements — before issues escalate.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-2 gap-3">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.07] border border-white/[0.08] backdrop-blur-sm">
                    <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                      <Icon className="h-4.5 w-4.5 text-blue-300" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white leading-tight">{f.label}</p>
                      <p className="text-[11px] text-blue-300">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-xs text-blue-300/70">Trusted by construction professionals across South Africa</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
